import { ObjectRepository } from "@api/repositories/ObjectRepository";
import { RatingRepository } from "@api/repositories/RatingRepository";
import { RepositoryFabric } from "@api/repositories/RepositoryFabric";
import { DateTime } from "luxon";
import { Element } from "@api/models/Element";
import { Store } from "vuex";
import { EventMembership } from "@api/models/EventMemberShip";
import { WebinarInfo } from "@api/models/WebinarInfo";
import { ProgramData } from "@share/program/types/ProgramData";
import { FetchDataParams } from '../types/FetchData'
import { AgendaDistributer } from "./agendaDistribution";
import {AgendaService} from "@share/program/data/AgendaService";
import {CommentsService} from "@share/program/data/CommentsService";
import {AgendaInfoService} from "@share/program/data/AgendaInfoService";
import { Rating } from "@api/models/Rating";

type ParallelRequestsResponseList = [
    Record<string, EventMembership[]>, {},
    { [key: string]: WebinarInfo } | undefined,
    Record<string, Rating> | undefined,
    Record<string, number> | undefined
];

class DataLoader {
    private readonly objectRepository: ObjectRepository;
    private readonly ratingRepository: RatingRepository;
    private readonly agendaService: AgendaService;
    private readonly commentsService: CommentsService;
    private readonly agendaInfoService: AgendaInfoService;

    agendaSchema: string;
    eventSchema: string;
    store: Store<any>;

    constructor(repositories: RepositoryFabric, store: Store<any>) {
        this.objectRepository = repositories.get(RepositoryFabric.OBJECT);
        this.agendaSchema = process.env.VUE_APP_AGENDA_SCHEMA!;
        this.eventSchema = process.env.VUE_APP_EVENTS_SCHEMA!;
        this.ratingRepository = repositories.get(RepositoryFabric.RATING);

        this.agendaService = new AgendaService(store,this.objectRepository);
        this.commentsService = new CommentsService(store, repositories.get(RepositoryFabric.COMMENT));
        this.agendaInfoService = new AgendaInfoService(
            store,
            this.objectRepository,
            repositories.get(RepositoryFabric.EVENT_MEMBERSHIP),
            repositories.get(RepositoryFabric.SESSION),
            this.ratingRepository,
            this.commentsService
        );

        this.store = store;
    }

    public async fetchAgendas(parameters: { eventId: string; dayStart: DateTime }, groupFilter: any, filterOffline: boolean) {
        return await this.agendaService.fetchForDate(parameters, groupFilter, filterOffline);
    }

    public async fetchSpeakers(ids: string[]) {
        return await this.agendaInfoService.fetchSpeakers(ids);
    }

    private async fetchEventLikes(eventId: string) {
        if (eventId) {
            try {
                const likes = await this.ratingRepository.query(this.eventSchema, [eventId]);
                return likes[0];
            } catch (e) {
                this.store.dispatch('reportException', e, { root: true });
            }
        }
    }

    async fetchData(
        { eventId, dayStart, dayEnd, filterOffline, loadEventData, loadAdditionalData }: FetchDataParams,
        groupFilter: any
    ): Promise<ProgramData> {

        const allAgendas: Element[] = await this.fetchAgendas({ eventId, dayStart }, groupFilter, filterOffline);

        const { today } = AgendaDistributer.distribute(allAgendas, dayStart, dayEnd);

        const allAgendasIds: string[] = allAgendas.map(item => item.id);
        const agendaIds: string[] = today.map(item => item.id);

        // RUN PARALLEL REQUESTS
        let speakers: { [key: string]: EventMembership[] } = {};
        let eventData = {};
        let webinarInfos: { [key: string]: WebinarInfo } | undefined;
        let likes;
        let comments;

        [speakers, eventData, webinarInfos, likes, comments] = await Promise.all([
            this.fetchSpeakers(allAgendasIds),
            loadEventData ? this.loadEventData(eventId) : eventData,
            ...(loadAdditionalData ? [
                this.agendaInfoService.fetchWebinarsInfo(agendaIds),
                this.agendaInfoService.fetchLikes(agendaIds),
                this.agendaInfoService.fetchCommentsCount(agendaIds)
            ] : [])
        ]) as ParallelRequestsResponseList;

        return {
            allAgendas,
            speakers,
            webinarInfos,
            likes,
            comments,
            ...eventData
        }
    }

    private async loadEventData(eventId: string) {
        let eventLikes: Rating | undefined;
        let eventComments: Record<string, number> | undefined;

        [eventLikes, eventComments] = await Promise.all([
            this.fetchEventLikes(eventId),
            this.commentsService.fetchCount([eventId], this.eventSchema)
        ])

        return {
            eventLikes,
            eventComments
        }
    }

    deleteAgenda(id: string) {
        return this.agendaService.deleteById(id);
    }
}

export { DataLoader }
